
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/api';
import { useUser } from '../context/UserContext';
import { notifyEmployeeFeedbackReady } from '../lib/notifications';

const B = {
  black: '#1e1e23', surface: '#26262d', surfaceLight: '#2e2e36',
  border: '#1e1a2e', borderLight: '#2e2850',
  purple: '#7B5CC8', purpleDim: '#4a3880', purpleDeep: '#1a1030',
  gold: '#C9A84C', goldDim: '#8a6e2a',
  textPrimary: '#ffffff', textSecondary: '#8888aa', textMuted: '#444466',
  green: '#1D9E75', greenDeep: '#0a1f15', greenDim: '#0f6e56',
  red: '#c0392b',
};

const RATINGS = [
  { v: 1, label: 'Unsatisfactory', color: '#c0392b', bg: '#0f0508' },
  { v: 2, label: 'Needs Improvement', color: '#C9A84C', bg: '#0f0d05' },
  { v: 3, label: 'Solid & Reliable', color: '#7B5CC8', bg: '#0e0b1a' },
  { v: 4, label: 'Strong Performer', color: '#1D9E75', bg: '#061510' },
  { v: 5, label: 'Outstanding', color: '#C9A84C', bg: '#100e05' },
];

const PRIORITY_META = [
  { key: 'must_do', label: 'Must do', tag: '#c0392b' },
  { key: 'should_do', label: 'Should do', tag: '#C9A84C' },
  { key: 'could_do', label: 'Could do', tag: '#1D9E75' },
];

const initials = n => n?.split(' ').map(x => x[0]).join('') || '?';

// Email addresses can legally contain `,` `(` `)` — all of which PostgREST
// treats as filter syntax in `.or(...)`. Wrap the value in double quotes
// and escape any embedded `"` as `""` so the filter parser sees a single
// string token rather than splitting on the commas/parens.
const pgEscape = s => `"${String(s).replace(/"/g, '""')}"`;

export default function ManagerReview() {
  const { employee } = useUser();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [employee]);

  const fetchData = async () => {
    if (!employee) return;
    setLoading(true);
    // Wrap in try/finally so any throw (network, JS bug, unimplemented
    // query method) still clears the spinner instead of stranding it.
    try {
      const { data: cycleData } = await supabase
        .from('review_cycles')
        .select('*')
        .eq('is_open', true)
        .eq('type', 'Annual')
        .single();

      if (!cycleData) return;
      setCycle(cycleData);

      // Direct reports for review purposes — uses the effective reviewer
      // (review_manager_email overrides manager_email when set; otherwise
      // fall back to the Okta manager). Mirrors the RLS policy so RLS and
      // app-level filtering agree on who sees whose review. Reviews are
      // scoped to user_type='Employee'; contractors / consultants appear on
      // the org chart but not here.
      const me = pgEscape(employee.email);
      const { data: reports } = await supabase
        .from('employees')
        .select('*')
        .or(`review_manager_email.eq.${me},and(review_manager_email.is.null,manager_email.eq.${me})`)
        .eq('user_type', 'Employee')
        .eq('active', true);

      if (!reports?.length) return;

      // Get their reviews
      const reportIds = reports.map(r => r.id);
      const { data: reviewData } = await supabase
        .from('annual_reviews')
        .select('*, employee:employee_id(*)')
        .in('employee_id', reportIds)
        .eq('cycle_id', cycleData.id);

      // Merge — include all reports, even those who haven't started
      const merged = reports.map(r => {
        const rev = reviewData?.find(rv => rv.employee_id === r.id);
        return { employee: r, review: rev || null };
      });

      setReviews(merged);
    } finally {
      setLoading(false);
    }
  };

  const openReview = (item) => {
    setSelected(item);
    setFeedback(item.review?.manager_feedback || '');
    setRating(item.review?.rating || null);
    setSubmitted(item.review?.manager_status === 'completed');
    setSavedAt(null);
    setSaveMsg('');
  };

  const flash = msg => { setSaveMsg(msg); setTimeout(() => setSaveMsg(''), 2500); };

  const saveDraft = async () => {
    if (!selected?.review || !feedback.trim()) return;
    setSaving(true);
    await supabase.from('annual_reviews').update({ manager_feedback: feedback, rating, updated_at: new Date().toISOString() }).eq('id', selected.review.id);
    const now = new Date();
    setSavedAt(`${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`);
    flash('Draft saved');
    setSaving(false);
  };

  const submitReview = async () => {
    if (!selected?.review || !feedback.trim() || !rating) return;
    setSaving(true);
    await supabase.from('annual_reviews').update({
      manager_feedback: feedback, rating,
      manager_status: 'completed',
      manager_reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }).eq('id', selected.review.id);

    await notifyEmployeeFeedbackReady({
      employeeEmail: selected.employee.email,
      managerName: employee.name,
      cycleLabel: cycle.label,
    });

    setSubmitted(true);
    setSaving(false);
    fetchData();
  };

  const pending = reviews.filter(r => r.review?.emp_status === 'submitted' && r.review?.manager_status !== 'completed').length;
  const completed = reviews.filter(r => r.review?.manager_status === 'completed').length;

  const Header = ({ sub }) => (
    <div style={{ background: B.black, borderBottom: `0.5px solid ${B.border}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {sub && <button onClick={() => { setSelected(null); setSubmitted(false); }} style={{ background: 'none', border: 'none', color: B.textSecondary, cursor: 'pointer', fontSize: 12, letterSpacing: '0.05em', padding: 0, marginRight: 8 }}>← BACK</button>}
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <img src="/logo-full.png" alt="xLight" style={{ height: 22, width: 'auto', display: 'block' }} />
        </button>
        <span style={{ width: 1, height: 16, background: B.border, margin: '0 8px' }} />
        <span style={{ fontSize: 11, color: B.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Manager Portal</span>
        {!sub && <button onClick={() => navigate('/')} style={{ marginLeft: 16, fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>← BACK TO PORTAL</button>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {savedAt && <span style={{ fontSize: 11, color: B.textMuted }}>Draft saved at {savedAt}</span>}
        {saveMsg && <span style={{ fontSize: 11, color: B.green }}>{saveMsg}</span>}
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 11, color: B.textSecondary, margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{cycle?.label}</p>
          <p style={{ fontSize: 12, color: B.textPrimary, margin: '2px 0 0' }}>{employee?.name}</p>
        </div>
      </div>
    </div>
  );

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: B.black, color: B.textSecondary, fontSize: 12, letterSpacing: '0.08em' }}>LOADING...</div>;

  // Submitted confirmation
  if (submitted && selected) return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header sub />
      <div style={{ padding: '3rem 2rem', textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purple}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: 20, color: B.purple }}>✓</div>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: B.textPrimary, marginBottom: 10 }}>Review submitted</h2>
        <p style={{ fontSize: 13, color: B.textSecondary, lineHeight: 1.7, marginBottom: 24 }}>{selected.employee.name} has been notified that their feedback is ready.</p>
        <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1rem 1.25rem', textAlign: 'left', marginBottom: 20 }}>
          <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 6, textTransform: 'uppercase' }}>Rating given</p>
          <p style={{ fontSize: 14, color: RATINGS.find(r => r.v === rating)?.color }}>{RATINGS.find(r => r.v === rating)?.label}</p>
        </div>
        <button onClick={() => { setSelected(null); setSubmitted(false); }} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 24px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: B.textPrimary, cursor: 'pointer' }}>BACK TO INBOX</button>
      </div>
    </div>
  );

  // Individual review
  if (selected) {
    const { employee: emp, review } = selected;
    const isCompleted = review?.manager_status === 'completed';
    const notSubmitted = !review || review.emp_status !== 'submitted';
    const activeRating = isCompleted ? review.rating : rating;
    const canSubmit = feedback.trim().length > 0 && rating;

    return (
      <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
        <Header sub />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem' }}>

          {/* Employee bar */}
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: B.purple }}>{initials(emp.name)}</div>
              <div>
                <p style={{ fontSize: 14, color: B.textPrimary, margin: 0 }}>{emp.name}</p>
                <p style={{ fontSize: 11, color: B.textSecondary, margin: '2px 0 0', letterSpacing: '0.04em' }}>{emp.department}</p>
              </div>
            </div>
            {isCompleted && <span style={{ fontSize: 10, letterSpacing: '0.06em', padding: '3px 10px', borderRadius: 99, background: B.purpleDeep, color: B.purple, border: `0.5px solid ${B.purpleDim}` }}>COMPLETED</span>}
          </div>

          {notSubmitted ? (
            <div style={{ background: '#0f0d05', border: `0.5px solid ${B.goldDim}`, borderRadius: 8, padding: '1rem 1.25rem', marginBottom: 24 }}>
              <p style={{ fontSize: 13, color: B.textSecondary, margin: 0 }}>This employee has not yet submitted their self-evaluation. You will be notified by email when it is ready for review.</p>
            </div>
          ) : (
            <>
              {/* Self eval */}
              <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 12, textTransform: 'uppercase' }}>Employee self-evaluation</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {[{ label: 'Key achievements & learnings', val: review?.achievements }, { label: 'Skills & development', val: review?.skills }].map(({ label, val }) => (
                  <div key={label} style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '12px 16px' }}>
                    <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 6, textTransform: 'uppercase' }}>{label}</p>
                    <p style={{ fontSize: 13, color: B.textPrimary, lineHeight: 1.7 }}>{val || '—'}</p>
                  </div>
                ))}
              </div>

              {/* Priorities */}
              <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 12, textTransform: 'uppercase' }}>Employee priorities</p>
              <div style={{ display: 'flex', gap: 10, marginBottom: 28 }}>
                {PRIORITY_META.map(({ key, label, tag }) => (
                  <div key={key} style={{ flex: 1, background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '10px 12px' }}>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: tag, color: '#fff', display: 'inline-block', marginBottom: 8, letterSpacing: '0.06em' }}>{label.toUpperCase()}</span>
                    <p style={{ fontSize: 12, color: B.textPrimary, lineHeight: 1.6, margin: 0 }}>{review?.[key] || '—'}</p>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: `0.5px solid ${B.border}`, marginBottom: 24 }} />

              {/* Rating */}
              <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 10, textTransform: 'uppercase' }}>Your assessment</p>
              <p style={{ fontSize: 12, color: B.textPrimary, marginBottom: 12 }}>Overall rating</p>

              {/* Results / Behavior context */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <div style={{ flex: 1, background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, letterSpacing: '0.08em', color: B.purple, fontWeight: 500 }}>50%</span>
                    <span style={{ fontSize: 10, letterSpacing: '0.08em', color: B.purple, textTransform: 'uppercase' }}>Results</span>
                  </div>
                  <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.5, margin: 0 }}>What is being achieved? Assess outcomes, goals met, and impact delivered against expectations.</p>
                </div>
                <div style={{ flex: 1, background: '#0f0d05', border: `0.5px solid ${B.goldDim}`, borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, letterSpacing: '0.08em', color: B.gold, fontWeight: 500 }}>50%</span>
                    <span style={{ fontSize: 10, letterSpacing: '0.08em', color: B.gold, textTransform: 'uppercase' }}>Behavior</span>
                  </div>
                  <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.5, margin: 0 }}>How it is being achieved? Assess against xLight's core values: Respect, Integrity, Innovation and Engineering Excellence.</p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
                {RATINGS.map(r => (
                  <button key={r.v} onClick={() => !isCompleted && setRating(r.v)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 6, border: `0.5px solid ${activeRating === r.v ? r.color : B.border}`, background: activeRating === r.v ? r.bg : 'transparent', cursor: isCompleted ? 'default' : 'pointer', textAlign: 'left', transition: 'all .15s' }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: `1.5px solid ${activeRating === r.v ? r.color : B.borderLight}`, background: activeRating === r.v ? r.color : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {activeRating === r.v && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                    </div>
                    <span style={{ fontSize: 13, color: activeRating === r.v ? r.color : B.textSecondary }}>{r.v} — {r.label}</span>
                  </button>
                ))}
              </div>

              {/* Feedback */}
              <p style={{ fontSize: 12, color: B.textPrimary, marginBottom: 6 }}>Manager feedback</p>
              <p style={{ fontSize: 12, color: B.textMuted, marginBottom: 10, lineHeight: 1.5 }}>Address both results and behavior in your comments. This will be shared with the employee.</p>
              {isCompleted
                ? <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '12px 16px', marginBottom: 24 }}>
                    <p style={{ fontSize: 13, color: B.textPrimary, lineHeight: 1.7 }}>{review.manager_feedback}</p>
                  </div>
                : <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={5}
                    placeholder="Share your assessment covering both what was achieved (results) and how it was achieved (behavior)..."
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, resize: 'vertical', padding: '10px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surfaceLight, color: B.textPrimary, fontFamily: 'inherit', lineHeight: 1.6, marginBottom: 16 }} />
              }

              {!isCompleted && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={saveDraft} disabled={!feedback.trim() || saving}
                      style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 20px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: feedback.trim() ? B.textSecondary : B.textMuted, cursor: feedback.trim() ? 'pointer' : 'not-allowed' }}>
                      {saving ? 'SAVING...' : 'SAVE DRAFT'}
                    </button>
                    {saveMsg && <span style={{ fontSize: 11, color: B.green }}>{saveMsg}</span>}
                  </div>
                  <button onClick={submitReview} disabled={!canSubmit || saving}
                    style={{ fontSize: 12, letterSpacing: '0.08em', padding: '10px 28px', borderRadius: 6, border: 'none', background: canSubmit ? B.purple : B.border, color: canSubmit ? '#fff' : B.textMuted, cursor: canSubmit ? 'pointer' : 'not-allowed', transition: 'all .2s' }}>
                    {saving ? 'SUBMITTING...' : 'SUBMIT REVIEW'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Inbox
  return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header />
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginBottom: 24 }}>
          {[{ label: 'Total reviews', val: reviews.length }, { label: 'Pending', val: pending, color: B.gold }, { label: 'Completed', val: completed, color: B.purple }].map(({ label, val, color }) => (
            <div key={label} style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1rem 1.25rem' }}>
              <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>{label}</p>
              <p style={{ fontSize: 22, color: color || B.textPrimary, margin: 0 }}>{val}</p>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {reviews.map(item => {
            const { employee: emp, review } = item;
            const isComp = review?.manager_status === 'completed';
            const rObj = isComp ? RATINGS.find(r => r.v === review.rating) : null;
            const statusColor = isComp ? B.purple : review?.emp_status === 'submitted' ? B.gold : B.textMuted;
            const statusBg = isComp ? B.purpleDeep : review?.emp_status === 'submitted' ? '#0f0d05' : B.surfaceLight;
            const statusBorder = isComp ? B.purpleDim : review?.emp_status === 'submitted' ? B.goldDim : B.border;
            const statusLabel = isComp ? 'COMPLETED' : review?.emp_status === 'submitted' ? 'PENDING' : 'NOT SUBMITTED';
            return (
              <button key={emp.id} onClick={() => openReview(item)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'border-color .15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = B.purpleDim}
                onMouseLeave={e => e.currentTarget.style.borderColor = B.border}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: B.purple, flexShrink: 0 }}>{initials(emp.name)}</div>
                  <div>
                    <p style={{ fontSize: 13, color: B.textPrimary, margin: 0 }}>{emp.name}</p>
                    <p style={{ fontSize: 11, color: B.textSecondary, margin: '2px 0 0', letterSpacing: '0.03em' }}>{emp.department}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {rObj && <span style={{ fontSize: 11, color: rObj.color }}>{rObj.label}</span>}
                  <span style={{ fontSize: 10, letterSpacing: '0.05em', padding: '2px 10px', borderRadius: 99, background: statusBg, color: statusColor, border: `0.5px solid ${statusBorder}` }}>{statusLabel}</span>
                  <span style={{ color: B.textMuted, fontSize: 13 }}>→</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
